/* Viretta – cart drawer behaviour (sections/cart-drawer.liquid). Rewritten 8 Oct 2026 from the two inline scripts of
   the LIVE theme (Impact 6.6) for Impact 7.2:
   - terms checkbox: remembered per browser, blocks checkout until ticked
   - page behind the open drawer is locked (iOS-safe body pinning)
   - upgrade row "Opgrader til et sæt": add buttons, arrows, empty slots filled from Shopify recommendations
     (section cart-drawer-upsell-recs)
   - product recommendations row (section cart-drawer-recommendations), only when the section setting is on
   - wheel / mouse-drag scrolling of the rows on desktop, arrows only when a row overflows
   Adds go through the theme's own cart events (cart:change), so the drawer, the header count and the free shipping
   bar update together; the old script swapped the whole section and left the header count stale.
   The theme re-renders the drawer's children on every cart change; one debounced observer on the drawer re-applies
   what a fresh render needs. Every listener is delegated, so nothing is bound twice. */
(() => {
  if (window.VirettaCartDrawer) return;
  window.VirettaCartDrawer = true;

  const TERMS_KEY = 'cart_terms_accepted';
  const TERMS_FALLBACK = 'Du musst die AGB akzeptieren, bevor du fortfahren kannst.';
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const drawerEl = () => document.getElementById('cart-drawer');

  const storage = {
    get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch (e) { /* storage blocked */ } }
  };

  /* ---------- Cart data: the theme's events carry the cart, so /cart.js is only fetched when needed ---------- */
  let cartPromise = null;
  const getCart = () => {
    if (!cartPromise) {
      cartPromise = fetch(`${root()}cart.js`).then((res) => res.json()).catch((err) => { cartPromise = null; throw err; });
    }
    return cartPromise;
  };
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    cartPromise = cart && cart.items ? Promise.resolve(cart) : null;
  });
  document.addEventListener('cart:refresh', () => { cartPromise = null; });

  /* ---------- Add a variant from the drawer (upgrade cards, empty-cart cards, recommendation "+") ---------- */
  const setBusy = (button, busy) => {
    if (!button) return;
    if (busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
  };

  const addVariant = async (variantId, button) => {
    if (!variantId || (button && button.getAttribute('aria-busy') === 'true')) return;
    setBusy(button, true);

    /* Same request the theme's product form makes: the cart drawer adds its own section id to the bundle */
    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections.join(','), sections_url: `${root()}variants/${variantId}` })
      });
      const json = await response.json();

      if (!response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      const cart = await (await fetch(`${root()}cart.js`)).json();
      cart.sections = json.sections;
      const items = Array.isArray(json.items) ? json.items : [json];

      document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (e) {
      window.location.reload();
    } finally {
      setBusy(button, false);
    }
  };

  /* ---------- Delegated clicks: add buttons and row arrows ---------- */
  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-upsell-variant-id]');
    if (addButton) {
      event.preventDefault();
      addVariant(addButton.getAttribute('data-upsell-variant-id'), addButton);
      return;
    }

    const arrow = event.target.closest('[data-upsell-scroll], [data-collection-scroll]');
    if (!arrow) return;

    const isUpsell = arrow.hasAttribute('data-upsell-scroll');
    const track = document.getElementById(isUpsell ? 'cart-drawer-upsells-carousel' : 'cart-drawer-collections-carousel');
    if (!track) return;
    const card = track.querySelector(isUpsell ? '.cart-upsell' : '.cart-collection');
    const step = card ? card.getBoundingClientRect().width + (isUpsell ? 12 : 10) : track.clientWidth;
    const direction = Number(arrow.getAttribute(isUpsell ? 'data-upsell-scroll' : 'data-collection-scroll'));
    track.scrollBy({ left: step * direction, behavior: 'smooth' });
  });

  /* ---------- Terms checkbox ---------- */
  document.addEventListener('change', (event) => {
    if (event.target.id !== 'terms-and-conditions') return;
    storage.set(TERMS_KEY, event.target.checked ? 'true' : 'false');
    if (event.target.checked) {
      const terms = event.target.closest('.cart-terms');
      const message = terms && terms.querySelector('.cart-terms__msg');
      if (message) message.remove();
      if (terms) terms.classList.remove('is-error');
    }
  });

  /* Capture phase, so checkout is stopped before anything else reacts to the submit */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches || !form.matches('#cart-drawer form.buy-buttons')) return;

    const checkbox = document.getElementById('terms-and-conditions');
    if (!checkbox || checkbox.checked) return;

    event.preventDefault();
    const terms = checkbox.closest('[data-terms-message]');
    /* Round 84: the message is shown under the checkbox instead of a browser alert() */
    const text = (terms && terms.getAttribute('data-terms-message')) || TERMS_FALLBACK;
    if (terms) {
      let message = terms.querySelector('.cart-terms__msg');
      if (!message) {
        message = document.createElement('p');
        message.className = 'cart-terms__msg';
        message.setAttribute('role', 'alert');
        terms.appendChild(message);
      }
      message.textContent = text;
      terms.classList.add('is-error');
    } else {
      window.alert(text);
    }

    /* The checkbox sits by the bottom checkout – bring it on screen when the top button was used */
    checkbox.scrollIntoView({ block: 'center' });

    form.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.removeAttribute('aria-busy');
      button.classList.remove('loading', 'is-busy');
      button.disabled = false;
    });

    storage.set(TERMS_KEY, 'false');
  }, true);

  const applyTermsState = (drawer) => {
    const checkbox = drawer.querySelector('#terms-and-conditions');
    if (checkbox) checkbox.checked = storage.get(TERMS_KEY) === 'true';
  };

  /* ---------- Page behind the open drawer stays put ----------
     iOS Safari ignores overflow:hidden on html/body, so the body is pinned with position:fixed. The scroll position is
     kept on the body itself, so a missed open/close can never leave a stale flag behind. */
  const syncBodyScrollLock = () => {
    const drawer = drawerEl();
    const isOpen = !!drawer && drawer.hasAttribute('open');
    const body = document.body;
    const isLocked = body.hasAttribute('data-cart-locked');

    document.documentElement.classList.toggle('cart-drawer-open', isOpen);

    if (isOpen && !isLocked) {
      const scrollY = window.scrollY || 0;
      body.setAttribute('data-cart-locked', String(scrollY));
      Object.assign(body.style, { position: 'fixed', top: `-${scrollY}px`, left: '0', right: '0', width: '100%' });
    } else if (!isOpen && isLocked) {
      const restoreTo = parseInt(body.getAttribute('data-cart-locked'), 10) || 0;
      body.removeAttribute('data-cart-locked');
      Object.assign(body.style, { position: '', top: '', left: '', right: '', width: '' });
      window.scrollTo(0, restoreTo);
    }
  };

  /* ---------- Desktop scrolling of the rows: wheel turns sideways, mouse drag ---------- */
  let drag = null;

  const enableDesktopScroll = (carousel) => {
    if (!carousel || carousel.dataset.desktopScroll) return;
    carousel.dataset.desktopScroll = 'true';
    carousel.style.cursor = 'grab';

    const snapType = getComputedStyle(carousel).scrollSnapType;
    let restoreTimer;
    const suspendSnap = () => { carousel.style.scrollSnapType = 'none'; clearTimeout(restoreTimer); };
    const restoreSnapSoon = () => {
      clearTimeout(restoreTimer);
      restoreTimer = setTimeout(() => { carousel.style.scrollSnapType = snapType; }, 150);
    };
    carousel._restoreSnapSoon = restoreSnapSoon;

    carousel.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      suspendSnap();
      carousel.scrollLeft += event.deltaY;
      restoreSnapSoon();
      event.preventDefault();
    }, { passive: false });

    carousel.addEventListener('mousedown', (event) => {
      suspendSnap();
      carousel.style.cursor = 'grabbing';
      drag = { carousel, startX: event.pageX, scrollLeft: carousel.scrollLeft };
    });

    carousel.addEventListener('mousemove', (event) => {
      if (!drag || drag.carousel !== carousel) return;
      event.preventDefault();
      carousel.scrollLeft = drag.scrollLeft - (event.pageX - drag.startX);
    });

    carousel.addEventListener('mouseleave', () => { if (drag && drag.carousel === carousel) endDrag(); });
  };

  const endDrag = () => {
    if (!drag) return;
    drag.carousel.style.cursor = 'grab';
    if (drag.carousel._restoreSnapSoon) drag.carousel._restoreSnapSoon();
    drag = null;
  };
  window.addEventListener('mouseup', endDrag);

  /* Arrows appear only when a row actually overflows */
  const markScrollable = () => {
    [['cart-drawer-collections-carousel', '.cart-collections'], ['cart-drawer-upsells-carousel', '.cart-upsells']].forEach(([id, wrapperSelector]) => {
      const track = document.getElementById(id);
      const wrapper = track && track.closest(wrapperSelector);
      if (wrapper) wrapper.setAttribute('data-scrollable', track.scrollWidth > track.clientWidth + 4 ? 'true' : 'false');
    });
  };
  window.addEventListener('resize', markScrollable);

  /* ---------- Shopify recommendations, fetched once per product/intent/limit AND cart contents per page view ----------
     Round 84c: the cards now depend on the cart (a lid per pan size in it), so the cart's variants are part of the key –
     the old key kept showing the 20 cm lids after a 24 cm pan was added. */
  const recsCache = new Map();
  let recsCartKey = '';
  const fetchRecs = (sectionId, productId, intent, limit) => {
    const key = [sectionId, productId, intent, limit, recsCartKey].join(':');
    if (!recsCache.has(key)) {
      const url = `${root()}recommendations/products?section_id=${sectionId}&product_id=${productId}&intent=${intent}&limit=${limit}`;
      recsCache.set(key, fetch(url, { cache: 'no-store' })
        .then((res) => (res.ok ? res.text() : ''))
        .catch(() => { recsCache.delete(key); return ''; }));
    }
    /* Fresh nodes every time: the drawer is re-rendered and the old cards go with it */
    return recsCache.get(key).then((html) => {
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.getElementById(`${sectionId}-results`);
    });
  };

  /* 'complementary' is empty unless it is set up in Search & Discovery, so fall back to 'related' */
  const fetchRecsWithFallback = async (sectionId, productId, intent, limit) => {
    const results = await fetchRecs(sectionId, productId, intent, limit);
    if ((!results || !results.children.length) && intent !== 'related') return fetchRecs(sectionId, productId, 'related', limit);
    return results;
  };

  /* As on LIVE, the last line of /cart.js drives the recommendations */
  const drivingProductId = (cart) => (cart && cart.items && cart.items.length ? cart.items[cart.items.length - 1].product_id : null);

  /* Upgrade row: slots the Liquid could not fill (data-recs-needed) are filled from recommendations */
  const fillUpsells = async (drawer, cart) => {
    const wrapper = drawer.querySelector('.cart-upsells[data-recs-needed]');
    if (!wrapper || wrapper.dataset.recsFilled) return;
    wrapper.dataset.recsFilled = 'true';

    const needed = parseInt(wrapper.getAttribute('data-recs-needed'), 10) || 0;
    const track = wrapper.querySelector('.cart-upsells__track');
    const productId = drivingProductId(cart);
    if (needed <= 0 || !track || !productId) return;

    recsCartKey = cart.items.map((item) => item.variant_id).join('-');
    const results = await fetchRecsWithFallback('cart-drawer-upsell-recs', productId, wrapper.getAttribute('data-recs-intent') || 'complementary', 10);
    if (!results || !wrapper.isConnected) return;

    const blocked = cart.items.map((item) => item.product_id);
    // UX review 10 Oct: never suggest another size, colour or coating of something already in the cart (same name before » – «)
    const base = (t) => String(t || '').split(/ [–-] /)[0].trim().toLowerCase();
    const inCart = cart.items.map((item) => base(item.product_title));
    track.querySelectorAll('[data-product-id]').forEach((card) => blocked.push(parseInt(card.getAttribute('data-product-id'), 10)));

    /* Round 84c: a card picked to fit a cart line (data-fit, e.g. the 24 cm lid) is counted per variant, so the same lid
       can appear once for every pan size in the cart */
    const inCartVariants = cart.items.map((item) => item.variant_id);
    let added = 0;
    Array.from(results.children).forEach((card) => {
      if (added >= needed) return;
      const variantCardId = parseInt(card.getAttribute('data-variant-id'), 10);
      const fitted = card.hasAttribute('data-fit');
      const id = fitted ? `v${variantCardId}` : parseInt(card.getAttribute('data-product-id'), 10);
      if (blocked.includes(id)) return;
      if (fitted && (inCartVariants.includes(variantCardId) || track.querySelector(`[data-variant-id="${variantCardId}"]`))) return;
      const t = card.querySelector('.cart-upsell__title');
      if (t && inCart.includes(base(t.textContent))) return;
      blocked.push(id);
      track.appendChild(card);
      added += 1;
    });

    if (added > 0) wrapper.hidden = false;
  };

  /* Recommendations row (only rendered when the section setting show_recommendations is on) */
  const loadRecommendations = async (drawer, cart) => {
    const wrapper = drawer.querySelector('#cart-drawer-recommendations');
    const target = wrapper && wrapper.querySelector('#cart-drawer-recommendations-target');
    if (!target || wrapper.dataset.recsLoaded) return;
    wrapper.dataset.recsLoaded = 'true';

    const productId = drivingProductId(cart);
    if (!productId) return;

    const results = await fetchRecsWithFallback('cart-drawer-recommendations', productId, wrapper.dataset.intent || 'related', wrapper.dataset.limit || 6);
    if (!results || !wrapper.isConnected) return;

    /* Nothing that is in the cart or already offered in the upgrade row */
    const blocked = cart.items.map((item) => item.product_id);
    drawer.querySelectorAll('.cart-upsells [data-product-id]').forEach((card) => blocked.push(parseInt(card.getAttribute('data-product-id'), 10)));

    Array.from(results.children).forEach((card) => {
      if (!blocked.includes(parseInt(card.getAttribute('data-product-id'), 10))) target.appendChild(card);
    });

    wrapper.hidden = target.children.length === 0;
    if (wrapper.hidden) return;

    /* Upgrade the placeholder div to Impact's scroll-carousel now that it has cards (it throws when empty) */
    let carousel = drawer.querySelector('#cart-drawer-recommendations-carousel');
    if (carousel && carousel.localName !== 'scroll-carousel') {
      const upgraded = document.createElement('scroll-carousel');
      Array.from(carousel.attributes).forEach((attr) => upgraded.setAttribute(attr.name, attr.value));
      upgraded.setAttribute('selector', '.horizontal-product');
      upgraded.append(...carousel.childNodes);
      carousel.replaceWith(upgraded);
      carousel = upgraded;
    }
    enableDesktopScroll(carousel);
  };

  /* ---------- One pass over a freshly rendered drawer ---------- */
  const syncDrawer = async () => {
    const drawer = drawerEl();
    if (!drawer) return;

    applyTermsState(drawer);
    enableDesktopScroll(drawer.querySelector('#cart-drawer-upsells-carousel'));
    markScrollable();

    const needsUpsells = drawer.querySelector('.cart-upsells[data-recs-needed]:not([data-recs-filled])');
    const needsRecs = drawer.querySelector('#cart-drawer-recommendations:not([data-recs-loaded])');
    if (!needsUpsells && !needsRecs) return;

    try {
      const cart = await getCart();
      /* Upgrade row first, so the recommendations row can skip what it shows */
      await fillUpsells(drawer, cart);
      await loadRecommendations(drawer, cart);
      markScrollable();
    } catch (e) { /* recommendations are optional */ }
  };

  let syncTimer;
  const scheduleSync = () => { clearTimeout(syncTimer); syncTimer = setTimeout(syncDrawer, 120); };

  /* ---------- Watch the drawer: content re-renders and its own open/close ---------- */
  let observedDrawer = null;
  const observer = new MutationObserver((mutations) => {
    let contentChanged = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        if (mutation.target === observedDrawer) syncBodyScrollLock();
      } else {
        contentChanged = true;
      }
    });
    if (contentChanged) scheduleSync();
  });

  const watchDrawer = () => {
    const drawer = drawerEl();
    if (!drawer || drawer === observedDrawer) return;
    observer.disconnect();
    observedDrawer = drawer;
    observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    syncBodyScrollLock();
    syncDrawer();
  };

  /* The theme editor replaces the whole section when a setting changes */
  document.addEventListener('shopify:section:load', () => { observedDrawer = null; watchDrawer(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();
})();

/* v2 drawer (9 Oct): the − / + buttons next to the quantity (snippets/v2-cd-line) only change the theme's quantity input and
   fire »change« – the theme's <line-item-quantity> then updates the cart and re-renders the drawer. Delegated on the
   document, because the drawer's children are replaced after every change. */
document.addEventListener('click', function (e) {
  var b = e.target.closest && e.target.closest('[data-v2-cdqty]');
  if (!b) return;
  var q = b.closest('line-item-quantity'); var inp = q && q.querySelector('.quantity-input');
  if (!inp) return;
  e.preventDefault();
  var step = parseInt(inp.getAttribute('step'), 10) || 1;
  var min = 0;
  var v = (parseInt(inp.value, 10) || 0) + parseInt(b.getAttribute('data-v2-cdqty'), 10) * step;
  var max = parseInt(inp.getAttribute('max'), 10); if (!isNaN(max)) v = Math.min(v, max);
  inp.value = Math.max(min, v);
  inp.dispatchEvent(new Event('change', { bubbles: true }));
});
